import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { ActionDialog, InfoDialog } from "@/components/dashboard/ActionDialog";
import { Cpu, Phone, MessagesSquare, CalendarCheck, Plus } from "lucide-react";
import { toast } from "sonner";

const ICONS = { "AI Voice Agent": Phone, "WhatsApp Automation": MessagesSquare, "Appointment Booker": CalendarCheck };

const SYSTEM_OPTIONS = [
  { value: "AI Voice Agent", label: "AI Voice Agent" },
  { value: "AI Chat System", label: "AI Chat System" },
  { value: "WhatsApp Automation", label: "WhatsApp Automation" },
  { value: "Appointment Booker", label: "Appointment Booker" },
  { value: "Omnichannel Router", label: "Omnichannel Router" },
];

export default function AISystems() {
  const [rows, setRows] = useState([]);
  const [openDeploy, setOpenDeploy] = useState(false);
  const [tuneTarget, setTuneTarget] = useState(null);   // deployment being tuned
  const [tuneScript, setTuneScript] = useState("");
  const [tuneBusy, setTuneBusy] = useState(false);
  const [transcripts, setTranscripts] = useState(null); // { deployment, rows }

  const refresh = () => api.get("/client/ai-systems").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);

  const openTune = (dep) => { setTuneTarget(dep); setTuneScript(dep.script || ""); };
  const saveTune = async () => {
    setTuneBusy(true);
    try {
      await api.patch(`/client/ai-systems/${tuneTarget.id}`, { script: tuneScript });
      toast.success("Script saved");
      setTuneTarget(null);
      refresh();
    } catch (e) {
      toast.error("Could not save");
    } finally { setTuneBusy(false); }
  };

  const openTranscripts = async (dep) => {
    try {
      const { data } = await api.get(`/client/ai-systems/${dep.id}/transcripts`);
      setTranscripts({ deployment: dep, rows: data });
    } catch { toast.error("Could not load transcripts"); }
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="AI Systems" subtitle="Live AI agents deployed across your operations." actions={<Button className="rounded-full" onClick={() => setOpenDeploy(true)} data-testid="ai-deploy-btn"><Plus className="size-4" /> Deploy new system</Button>} />

      <ActionDialog
        open={openDeploy}
        onOpenChange={setOpenDeploy}
        title="Deploy a new AI system"
        description="Spin up a new AI deployment. Goes into 'provisioning' status while our onboarding team configures the script."
        endpoint="/client/ai-systems"
        submitLabel="Deploy"
        testidPrefix="deploy"
        onSuccess={refresh}
        fields={[
          { name: "system", label: "System", required: true, type: "select", options: SYSTEM_OPTIONS, defaultValue: "AI Voice Agent" },
          { name: "channel", label: "Channel", required: true, type: "select", defaultValue: "Phone", options: [{ value: "Phone", label: "Phone" }, { value: "WhatsApp", label: "WhatsApp" }, { value: "Web Chat", label: "Web Chat" }, { value: "Calendar", label: "Calendar" }] },
        ]}
      />

      {/* Tune script dialog */}
      <InfoDialog
        open={!!tuneTarget}
        onOpenChange={(v) => !v && setTuneTarget(null)}
        title={`Tune script — ${tuneTarget?.system || ""}`}
        description="Edit the FCA-aware system prompt this AI uses for qualification."
        testid="tune-dialog"
      >
        <textarea
          value={tuneScript}
          onChange={(e) => setTuneScript(e.target.value)}
          rows={10}
          className="w-full font-mono text-xs rounded-md border border-input bg-background p-3"
          data-testid="tune-script-textarea"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setTuneTarget(null)}>Cancel</Button>
          <Button onClick={saveTune} disabled={tuneBusy} data-testid="tune-save">{tuneBusy ? "Saving…" : "Save script"}</Button>
        </div>
      </InfoDialog>

      {/* Transcripts dialog */}
      <InfoDialog
        open={!!transcripts}
        onOpenChange={(v) => !v && setTranscripts(null)}
        title={`Recent transcripts — ${transcripts?.deployment?.system || ""}`}
        description={`${transcripts?.rows?.length || 0} sessions handled recently.`}
        testid="transcripts-dialog"
      >
        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {(transcripts?.rows || []).map((t) => (
            <div key={t.id} className="rounded-md border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest text-slate-500">{t.qualified ? "Qualified" : "In-progress"}</span>
                <span className="text-[11px] font-mono text-slate-500">{t.views || 0} views</span>
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900 truncate">{t.qualification?.intent || "—"}</div>
              <div className="mt-2 space-y-1">
                {(t.messages || []).map((m, i) => (
                  <div key={i} className="text-xs text-slate-700"><span className="font-mono text-slate-400 mr-2">{m.role}</span>{m.text}</div>
                ))}
              </div>
            </div>
          ))}
          {(!transcripts?.rows || transcripts.rows.length === 0) && <div className="text-sm text-slate-500 text-center py-6">No transcripts yet.</div>}
        </div>
      </InfoDialog>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.map((a) => {
          const Icon = ICONS[a.system] || Cpu;
          return (
            <Card key={a.id} className="rounded-xl shadow-soft border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-900 text-white"><Icon className="size-5" strokeWidth={1.6} /></span>
                  <span className={`inline-flex items-center gap-1.5 text-xs ${a.status === "active" ? "text-emerald-700" : "text-amber-700"}`}>
                    <span className={`size-1.5 rounded-full ${a.status === "active" ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`} />
                    {a.status}
                  </span>
                </div>
                <h3 className="mt-5 font-medium text-slate-900">{a.system}</h3>
                <div className="mt-1 text-xs text-slate-500">Channel · {a.channel}</div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <Stat l="Handled" v={(a.calls_handled || 0).toLocaleString()} />
                  <Stat l="Booked" v={(a.booked || 0).toLocaleString()} />
                </div>
                <div className="mt-5 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 rounded-full" onClick={() => openTranscripts(a)} data-testid={`ai-transcripts-${a.id}`}>Transcripts</Button>
                  <Button variant="outline" size="sm" className="flex-1 rounded-full" onClick={() => openTune(a)} data-testid={`ai-tune-${a.id}`}>Tune script</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ l, v }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div>
      <div className="mt-0.5 text-xl font-semibold text-slate-900 tabular-nums">{v}</div>
    </div>
  );
}
